import PhoneInput from "../../components/PhoneInput";
import { toPhoneDigits } from "../../helper/phoneUtils";
import Spinner from "../../components/Spinner";
import { ChangeEvent, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { EligiblePatient } from "./EligibilityGrid";
import { capitalizeInitial } from "../../helper/TextUtils";
import AsyncTypeahead, { DataListType } from "../../components/AsyncTypeahead";
import { createUsersApi } from "../../api-helpers/UserApiHelper";
import { createEligiblesApi } from "../../api-helpers/PatientApiHelper";
import { createLocationApi, createWorkflowApi } from "../../api-helpers/CommonUtilsApiHelper";
import { IdValuePairDTO, WorkflowState } from "common-util-experience-client";
import { WorkflowConstants } from "../../helper/WorkflowConstants";
import { workflowStatesCache } from "../../grid/renderers/WorkflowStateCellValueGetter";
import { Section, ReadField, SectionNavLayout } from "../../components/FormSections";
import { ToastConfig } from "../../components/Toaster";
import { UserNameResolver } from "../../grid/resolvers/UserNameResolver";
import { formatDate } from "../../helper/dateFormatter";

interface EditEligiblePatientProps {
  eligiblePatient: EligiblePatient;
  setOffCanvasShow: (data: boolean) => void;
  handleEnrollPatient: (eligibleId: string) => void;
  showToaster: (toasterConfig: ToastConfig) => void
  reloadGrid?: () => void;
}

export const EditEligiblePatient = ({
  eligiblePatient,
  setOffCanvasShow,
  handleEnrollPatient,
  showToaster,
  reloadGrid,
}: EditEligiblePatientProps) => {

  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [form, setForm] = useState({
    ...eligiblePatient,
    reachDate: formatDate(eligiblePatient.reachDate),
    lastOutReachDate: formatDate(eligiblePatient.lastOutReachDate),
    phone: toPhoneDigits(eligiblePatient.phoneNumber),
    mobilePhone: toPhoneDigits(eligiblePatient.phoneNumber2),
    assignedUserId: eligiblePatient.assignedUserId || "",
    statusId: eligiblePatient.currentStatus || "",
    comments: "",
    zipCode: "",
    cityId: "",
    stateId: "",
  });
  const [eligibilityStatuses, setEligibilityStatuses] = useState<WorkflowState[]>([]);
  const [coachList, setCoachList] = useState<DataListType[]>([]);
  const [validCoach, setValidCoach] = useState<boolean>(true);
  const [selectedCity, setSelectedCity] = useState<string>("");
  const [selectedState, setSelectedState] = useState<string>("");
  const [invalidZipCode, setInvalidZipCode] = useState<boolean>(false);
  const [cityList, setCityList] = useState<IdValuePairDTO[]>([]);
  const usersApi = createUsersApi()
  const WorkflowApi = createWorkflowApi();
  const eligiblesApi = createEligiblesApi();
  const locationApi = createLocationApi();

  useEffect(() => {
    loadCoachesList("");
  }, []);

  useEffect(() => {
    // Initialize form state with eligiblePatient data when the component mounts or eligiblePatient changes
    setForm({
      ...eligiblePatient,
      reachDate: formatDate(eligiblePatient.reachDate),
      lastOutReachDate: formatDate(eligiblePatient.lastOutReachDate),
      phone: toPhoneDigits(eligiblePatient.phoneNumber),
      assignedUserId: eligiblePatient.assignedUserId || "",
      statusId: eligiblePatient.currentStatus || "",
      comments: "",
      addressLine1: eligiblePatient.addressLine1 || "",
      mobilePhone: toPhoneDigits(eligiblePatient.phoneNumber2),
      addressLine2: eligiblePatient.addressLine2 || "",
      zipCode: eligiblePatient.zipCode || "",
      cityId: eligiblePatient.cityId || "",
      stateId: eligiblePatient.stateId || "",
      partnerName: eligiblePatient.partnerName || "",
      numberOfAttempts: eligiblePatient.numberOfAttempts || 0,
      email: eligiblePatient.email || "",
    });
    if (eligiblePatient.stateId && eligiblePatient.cityId && eligiblePatient.stateId !== '' && eligiblePatient.cityId !== '') {
      fetchLocationData(eligiblePatient.stateId, eligiblePatient.cityId);
    }
  }, [eligiblePatient]);

  const loadCoachesList = async (query: string) => {
    if (!query) {
      setCoachList([]);
      return;
    }
    setLoading(true);
    try {
      const response = await usersApi.usersControllerSearchUsers(undefined, query)
      const results = response.data.map((result: any) => ({
        key: result.key, // Use id as the key
        value: result.value, // Display name as the value
      }));
      setCoachList(results);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationData = async (stateId: string, cityId: string) => {
    const stateName = await locationApi.locationControllerFindState(stateId);
    setSelectedState(stateName.data.value);
    const cityName = await locationApi.locationControllerFindCity(stateId, cityId);
    setSelectedCity(cityName.data.value);
  }

  const fetchZipcodeDetails = async (zipcode: string, fieldName: string) => {
    try {
      setLoading(true);
      const response = await locationApi.locationControllerGetZipcodeDetails(
        zipcode
      );
      const { state, cities } = response.data;
      if (cities.length === 1) {
        if (fieldName === 'zipCode') {
          setCityList([]);
          setInvalidZipCode(false);
          setSelectedCity(cities[0].value);
          setSelectedState(state.value);
          setForm((prevForm) => ({
            ...prevForm,
            stateId: state.id,
            cityId: cities[0].id
          }));
        }
      } else if (cities.length > 1) {
        if (fieldName === 'zipCode') {
          setInvalidZipCode(false);
          setCityList(cities);
          setSelectedState(state.value);
          setForm((prevForm) => ({
            ...prevForm,
            cityId: '',
            stateId: state.id,
          }));
        }
        setForm((prevForm) => ({
          ...prevForm,
          stateId: state.id,
        }));
      } else {
        // Handle case where no cities are returned
        console.error("No cities found for this zipcode");
      }
    } catch (error) {
      if (fieldName === 'zipCode') {
        setInvalidZipCode(true);
        setCityList([]);
        setForm((prevForm) => ({
          ...prevForm,
          stateId: '',
          cityId: '',
        }));
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadEligibilityStatuses = async () => {
    try {
      const { data } = await WorkflowApi.workflowControllerGetStatesForWorkflow(WorkflowConstants.eligibility);
      setEligibilityStatuses(data);
    } catch (error) {
      console.log(error);
    }
  };

  // Validates the fields that matter regardless of whether this save also logs an attempt.
  // Returns false (and sets custom validity messages) when something is invalid.
  const validateContactFields = () => {
    const phoneInput = document.querySelector('[name="phone"]') as HTMLInputElement;
    const phone2Input = document.querySelector('[name="mobilePhone"]') as HTMLInputElement;
    const zipcodeInput = document.querySelector('[name="zipCode"]') as HTMLInputElement;
    const emailInput = document.querySelector('[name="email"]') as HTMLInputElement;
    phoneInput.setCustomValidity("");
    phone2Input.setCustomValidity("");
    zipcodeInput.setCustomValidity("");
    emailInput.setCustomValidity("");

    let valid = !invalidZipCode;

    if (form.phone && form?.phone?.length !== 10) {
      phoneInput.setCustomValidity("Invalid Phone Number");
      valid = false;
    }
    if (form.mobilePhone && form?.mobilePhone?.length !== 10) {
      phone2Input.setCustomValidity("Invalid Phone Number");
      valid = false;
    }
    if (form.zipCode && form?.zipCode?.length !== 5) {
      zipcodeInput.setCustomValidity("Invalid Zipcode");
      valid = false;
    }
    if (form.email && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.email)) {
      emailInput.setCustomValidity("Invalid Email");
      valid = false;
    }

    return valid;
  };

  const buildUpdatePayload = () => ({
    patientExternalIdFromPartner: form.patientExternalIdFromPartner || "",
    phone: form.phone || "",
    mobilePhone: form.mobilePhone || "",
    email: form.email || "",
    addressLine1: form.addressLine1 || "",
    addressLine2: form.addressLine2 || "",
    cityId: form.cityId || "",
    stateId: form.stateId || "",
    zipCode: form.zipCode || "",
    assignedUserId: form.assignedUserId || "",
    pcpFirstName: form.pcpFirstName || "",
    pcpLastName: form.pcpLastName || "",
    statusId: form.statusId || "",
    comments: form.comments || "",
  });

  const submitUpdate = async (logAttempt: boolean) => {
    try {
      await eligiblesApi.eligiblesControllerUpdateEligibles(
        { ...buildUpdatePayload(), logAttempt },
        eligiblePatient.id
      );
      setOffCanvasShow(false);
      reloadGrid && reloadGrid();
      showToaster({
        type: "success",
        title: logAttempt
          ? "Eligible patient updated and attempt logged"
          : "Edits saved successfully",
        icon: "check_circle_outline",
      });
    } catch (error) {
      showToaster({
        type: "danger",
        title: "Failed to update eligible patient",
        icon: "error_outline",
      });
      console.error(error);
    }
  };

  // "Save & Log Attempt" - requires notes + status, same as the original Save behavior.
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const notesTextarea = document.querySelector('[name="comments"]') as HTMLTextAreaElement;
    notesTextarea.setCustomValidity("");

    event.preventDefault();
    setValidated(true);

    const contactFieldsValid = validateContactFields();

    if (!form.comments.trim()) {
      notesTextarea.reportValidity();
      notesTextarea.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    if (!contactFieldsValid || !form.comments.trim()) {
      return;
    }

    const formTarget = event.currentTarget;
    if (formTarget.checkValidity() === false) {
      event.stopPropagation();
      return;
    }

    await submitUpdate(true);
  };

  // "Save Edits" - saves contact/demographic edits without requiring notes/status
  // and without creating a new outreach attempt record.
  const handleSaveEdits = async () => {
    if (!validateContactFields()) {
      return;
    }

    await submitUpdate(false);
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = event.target;
    if (type === "tel") {
      // PhoneInput already emits bare digits, capped at PHONE_DIGIT_LENGTH.
      setForm({
        ...form,
        [name]: toPhoneDigits(value),
      });
      return;
    }
    if (name === "zipCode") {
      if (value.length === 5) {
        fetchZipcodeDetails(value, name);
      } else {
        const zipcodeInputField = document.querySelector('[name="zipCode"]') as HTMLInputElement;
        zipcodeInputField.setCustomValidity("");
        setInvalidZipCode(false);
        setSelectedCity('');
        setSelectedState('');
        setCityList([]);
        setForm((prevForm) => ({
          ...prevForm,
          cityId: '',
          stateId: '',
        }));
      }
    }
    setForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));
  };

  const selectCoach = (selected: DataListType) => {
    setForm({
      ...form,
      assignedUserId: selected && selected.key,
    });
  };

  useEffect(() => {
    loadEligibilityStatuses();
  }, [])

  return (
    <>
      {loading && <Spinner />}
      <Form
        className="d-flex flex-column h-100"
        noValidate
        validated={validated}
        onSubmit={handleSubmit}
      >
        <SectionNavLayout>
            <Section title="Patient">
              <ReadField
                className="col-6"
                label="First Name"
                value={capitalizeInitial(form?.firstName || '')}
              />
              <ReadField
                className="col-6"
                label="Last Name"
                value={capitalizeInitial(form?.lastName || '')}
              />
              <div className="col-6">
                <label className="form-label fs-12 fw-semibold mb-1">Phone</label>
                <PhoneInput
                  name="phone"
                  value={form?.phone || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-6">
                <label className="form-label fs-12 fw-semibold mb-1">Phone 2</label>
                <PhoneInput
                  name="mobilePhone"
                  value={form?.mobilePhone || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-12">
                <label className="form-label fs-12 fw-semibold mb-1">Email Address</label>
                <input
                  name="email"
                  type="text"
                  className="form-control"
                  placeholder="Enter email address"
                  value={form?.email || ''}
                  onChange={handleChange}
                />
              </div>
            </Section>

            <Section title="Mailing Address">
              <div className="col-12">
                <label className="form-label fs-12 fw-semibold mb-1">Address Line</label>
                <input
                  type="text"
                  className="form-control lh-1"
                  placeholder="Address Line 1"
                  onChange={handleChange}
                  name="addressLine1"
                  value={form.addressLine1 || ''} />
              </div>
              <div className="col-12">
                <input
                  type="text"
                  className="form-control lh-1"
                  placeholder="Address Line 2"
                  onChange={handleChange}
                  name="addressLine2"
                  value={form.addressLine2 || ''} />
              </div>
              <div className="col-4">
                <label className="form-label fs-12 fw-semibold mb-1">City</label>
                {cityList.length === 0 ? (
                  <div className="form-control-plaintext">
                    {selectedCity ? selectedCity : <span className="text-light">City</span>}
                  </div>
                ) : (
                  <select
                    className="form-select"
                    name="cityId"
                    value={form.cityId || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {cityList.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.value}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="col-4">
                <label className="form-label fs-12 fw-semibold mb-1">State</label>
                <div className="form-control-plaintext">
                  {selectedState ? selectedState : <span className="text-light">State</span>}
                </div>
              </div>
              <div className="col-4">
                <label className="form-label fs-12 fw-semibold mb-1">Zipcode</label>
                <input
                  type="text"
                  className={`form-control lh-1 ${invalidZipCode && form.zipCode !== '' ? "border-red" : ""
                    }`}
                  placeholder="Zipcode"
                  onChange={handleChange}
                  maxLength={5}
                  name="zipCode"
                  value={form.zipCode || ''}
                />
                {invalidZipCode && form.zipCode !== '' ? (
                  <small style={{ color: "red" }}>Invalid Zipcode</small>
                ) : (
                  ""
                )}
              </div>
            </Section>

            <Section title="Partner & Pathway">
              <ReadField className="col-6" label="Partner" value={form.partnerName} />
              <div className="col-6">
                <label className="form-label fs-12 fw-semibold mb-1">
                  External Patient ID from Partner
                </label>
                <input
                  name="patientExternalIdFromPartner"
                  type="text"
                  className="form-control"
                  placeholder="External Patient ID from Partner"
                  value={form.patientExternalIdFromPartner || ''}
                  onChange={handleChange}
                />
              </div>
              <ReadField
                className="col-12"
                label="Pathway"
                value={workflowStatesCache.get(form.pathwayDesignerStateId || '')}
              />
            </Section>

            <Section title="Care Team">
              <div className="col-6">
                <label className="form-label fs-12 fw-semibold mb-1">PCP First Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="PCP First Name"
                  value={capitalizeInitial(form?.pcpFirstName || '')}
                  onChange={handleChange}
                  name='pcpFirstName'
                />
              </div>
              <div className="col-6">
                <label className="form-label fs-12 fw-semibold mb-1">PCP Last Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="PCP Last Name"
                  value={capitalizeInitial(form.pcpLastName || '')}
                  onChange={handleChange}
                  name='pcpLastName'
                />
              </div>
              <div className='col-12'>
                <label
                  htmlFor="assignedUserId"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Assignee
                </label>
                <AsyncTypeahead
                  name="assignedUserId"
                  addClass={'form-control'}
                  placeholder={"Search Assignee"}
                  options={coachList}
                  onSelect={selectCoach}
                  callback={loadCoachesList}
                  defaultValue={UserNameResolver(form?.assignedUserId)}
                  isValid={validCoach}
                // disabled
                />
              </div>
            </Section>

            <Section title="Status">
              <ReadField className="col-4" label="Reach Date" value={form.reachDate} />
              <ReadField className="col-4" label="Last Outreach Date" value={form.lastOutReachDate} />
              <ReadField className="col-4" label="No. of Attempts" value={form?.numberOfAttempts} />
              <div className="col-12">
                <label className="form-label fs-12 fw-semibold mb-1">Current Status</label>
                <select
                  className='form-select fs-12'
                  name="statusId"
                  id="statusId"
                  value={form.statusId || ''}
                  onChange={handleChange}
                  required={true}
                >
                  <option value='' >Select Status</option>
                  {eligibilityStatuses?.map((status: WorkflowState, index: number) => (
                    <option className="fs-12" key={index} value={status.stateId}>
                      {status.stateName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <label htmlFor="Notes" className="form-label fs-12 fw-semibold mb-1">Notes</label>
                <textarea
                  name="comments"
                  className="form-control form-control-sm"
                  id="Notes"
                  placeholder="Add relevant comments"
                  rows={4}
                  required
                  onChange={handleChange}
                  value={form.comments}
                />
              </div>
            </Section>
        </SectionNavLayout>
        <div className="offcanvas-footer w-100 border-top d-flex justify-content-between px-4 py-3">
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary rounded-pill px-5 fs-13"
              type="reset"
              onClick={() => setOffCanvasShow(false)}
            >
              Discard
            </button>

            <button
              type="button"
              className="btn btn-outline-secondary rounded-pill px-5 fs-13"
              onClick={handleSaveEdits}
            >
              Save Edits
            </button>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary rounded-pill px-5 fs-13"
              onClick={() => handleEnrollPatient(eligiblePatient.id)}
            >
              Continue Enrollment
            </button>

            <button
              className="btn btn-secondary rounded-pill px-5 fs-13"
              type="submit"
            >
              Save &amp; Log Attempt
            </button>
          </div>
        </div>
      </Form>
    </>
  );
};
